import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  Search,
  Filter,
  MoreVertical,
  Ban,
  Tag,
  User,
  MessageSquare,
  Inbox,
  RefreshCw
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useSocialInbox, useSocialMessages } from '@/hooks/social';
import { SocialAccount, SocialContactWithDetails, SocialPlatform, PLATFORM_INFO, SocialInboxFilters } from '@/types/social';
import { cn } from '@/lib/core/utils';
import { ConversationThread } from './ConversationThread';

interface UnifiedInboxProps {
  businessId: string;
  accounts: SocialAccount[];
}

export const UnifiedInbox: React.FC<UnifiedInboxProps> = ({
  businessId,
  accounts
}) => {
  const {
    conversations,
    isLoading,
    error,
    selectedConversation,
    selectConversation,
    markAsRead,
    blockContact,
    refresh,
    filters,
    updateFilters
  } = useSocialInbox(businessId);

  const {
    messages,
    isLoading: messagesLoading,
    sendMessage,
    markAsRead: markMessagesAsRead,
    hasMore,
    loadMore
  } = useSocialMessages(businessId, selectedConversation?.id || null);

  // Mark messages as read when selecting a conversation
  useEffect(() => {
    if (selectedConversation?.id && selectedConversation.unread_count > 0) {
      markAsRead(selectedConversation.id);
      markMessagesAsRead();
    }
  }, [selectedConversation?.id]);

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  const getInitials = (name: string | null, username: string | null) => {
    const text = name || username || '?';
    return text.slice(0, 2).toUpperCase();
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  // Filter platforms that support DMs
  const dmPlatforms = accounts
    .filter(a => a.status === 'active' && PLATFORM_INFO[a.platform].supportsDMs)
    .map(a => a.platform);

  const hasConnectedDMAccounts = dmPlatforms.length > 0;

  if (!hasConnectedDMAccounts) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Unified Inbox</h2>
            <p className="text-muted-foreground">
              Manage messages from all your social media accounts
            </p>
          </div>
        </div>

        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Connect an Account First</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              Connect an Instagram or Facebook account to start receiving and managing messages.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Unified Inbox</h2>
          <p className="text-muted-foreground">
            {totalUnread > 0 ? `${totalUnread} unread message${totalUnread !== 1 ? 's' : ''}` : 'All messages read'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-280px)] min-h-[500px]">
        {/* Conversation List */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  className="pl-9"
                  value={filters.search || ''}
                  onChange={(e) => updateFilters({ search: e.target.value })}
                />
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                <Select
                  value={filters.platform || 'all'}
                  onValueChange={(v) => updateFilters({ platform: v === 'all' ? undefined : v as SocialPlatform })}
                >
                  <SelectTrigger className="w-[140px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    {dmPlatforms.map(platform => (
                      <SelectItem key={platform} value={platform}>
                        {PLATFORM_INFO[platform].name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant={filters.unreadOnly ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateFilters({ unreadOnly: !filters.unreadOnly })}
                >
                  Unread only
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {isLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex gap-3 animate-pulse">
                      <div className="h-10 w-10 rounded-full bg-muted" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 bg-muted rounded" />
                        <div className="h-3 w-1/2 bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {filters.search || filters.platform || filters.unreadOnly
                      ? 'No conversations match your filters'
                      : 'No conversations yet'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {conversations.map((conversation) => (
                    <ConversationListItem
                      key={conversation.id}
                      conversation={conversation}
                      isSelected={selectedConversation?.id === conversation.id}
                      onClick={() => selectConversation(conversation.id)}
                      onBlock={() => blockContact(conversation.id)}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Conversation Thread */}
        <Card className="lg:col-span-2 flex flex-col">
          {selectedConversation ? (
            <ConversationThread
              contact={selectedConversation}
              messages={messages}
              isLoading={messagesLoading}
              hasMore={hasMore}
              onLoadMore={loadMore}
              onSendMessage={sendMessage}
              onBlockContact={() => {
                blockContact(selectedConversation.id);
                selectConversation(null);
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold">Select a conversation</h3>
                <p className="text-sm text-muted-foreground">
                  Choose a conversation from the list to start messaging
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// Conversation List Item Component
interface ConversationListItemProps {
  conversation: SocialContactWithDetails;
  isSelected: boolean;
  onClick: () => void;
  onBlock: () => void;
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isSelected,
  onClick,
  onBlock
}) => {
  const Icon = conversation.platform === 'instagram' ? Instagram
    : conversation.platform === 'facebook' ? Facebook
    : conversation.platform === 'linkedin' ? Linkedin
    : Video;

  const platformInfo = PLATFORM_INFO[conversation.platform];

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 cursor-pointer transition-colors hover:bg-accent/50",
        isSelected && "bg-accent"
      )}
      onClick={onClick}
    >
      <div className="relative">
        <Avatar className="h-10 w-10">
          <AvatarImage src={conversation.profile_picture_url || undefined} />
          <AvatarFallback>
            {(conversation.display_name || conversation.username || '?').slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div
          className="absolute -bottom-1 -right-1 rounded-full p-0.5"
          style={{ backgroundColor: platformInfo.bgColor }}
        >
          <Icon className="h-3 w-3" style={{ color: platformInfo.color }} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={cn(
            "font-medium truncate",
            conversation.unread_count > 0 && "font-semibold"
          )}>
            {conversation.display_name || conversation.username || 'Unknown'}
          </span>
          {conversation.last_message && (
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(conversation.last_message.sent_at), { addSuffix: false })}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className={cn(
            "text-sm truncate",
            conversation.unread_count > 0 ? "text-foreground" : "text-muted-foreground"
          )}>
            {conversation.last_message?.content || 'No messages yet'}
          </p>
          {conversation.unread_count > 0 && (
            <Badge variant="default" className="h-5 min-w-[20px] flex items-center justify-center rounded-full">
              {conversation.unread_count}
            </Badge>
          )}
        </div>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <User className="h-4 w-4 mr-2" />
            View Profile
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Tag className="h-4 w-4 mr-2" />
            Manage Tags
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); onBlock(); }}>
            <Ban className="h-4 w-4 mr-2" />
            Block Contact
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UnifiedInbox;
